import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, Plus, Search, Edit2, Trash2, X, MapPin, Users, CreditCard, Globe } from 'lucide-react';
import './CompanyManagement.css';

export const CURRENCY_OPTIONS = [
    { code: 'USD', symbol: '$', name: 'USD - US Dollar ($)' },
    { code: 'EUR', symbol: '€', name: 'EUR - Euro (€)' },
    { code: 'INR', symbol: '₹', name: 'INR - Indian Rupee (₹)' },
    { code: 'GBP', symbol: '£', name: 'GBP - British Pound (£)' },
    { code: 'CAD', symbol: 'CA$', name: 'CAD - Canadian Dollar (CA$)' },
    { code: 'AUD', symbol: 'A$', name: 'AUD - Australian Dollar (A$)' },
    { code: 'JPY', symbol: '¥', name: 'JPY - Japanese Yen (¥)' },
    { code: 'CNY', symbol: 'CN¥', name: 'CNY - Chinese Yuan (CN¥)' },
    { code: 'AED', symbol: 'AED', name: 'AED - UAE Dirham (AED)' },
    { code: 'SGD', symbol: 'S$', name: 'SGD - Singapore Dollar (S$)' },
    { code: 'CHF', symbol: 'CHF', name: 'CHF - Swiss Franc (CHF)' },
    { code: 'HKD', symbol: 'HK$', name: 'HKD - Hong Kong Dollar (HK$)' },
    { code: 'NZD', symbol: 'NZ$', name: 'NZD - New Zealand Dollar (NZ$)' },
    { code: 'SEK', symbol: 'kr', name: 'SEK - Swedish Krona (kr)' },
    { code: 'NOK', symbol: 'kr', name: 'NOK - Norwegian Krone (kr)' },
    { code: 'DKK', symbol: 'kr', name: 'DKK - Danish Krone (kr)' },
    { code: 'MXN', symbol: 'Mex$', name: 'MXN - Mexican Peso (Mex$)' },
    { code: 'BRL', symbol: 'R$', name: 'BRL - Brazilian Real (R$)' },
    { code: 'ZAR', symbol: 'R', name: 'ZAR - South African Rand (R)' },
    { code: 'KRW', symbol: '₩', name: 'KRW - South Korean Won (₩)' },
    { code: 'SAR', symbol: 'SR', name: 'SAR - Saudi Riyal (SR)' },
    { code: 'QAR', symbol: 'QR', name: 'QAR - Qatari Riyal (QR)' },
    { code: 'KWD', symbol: 'KD', name: 'KWD - Kuwaiti Dinar (KD)' },
    { code: 'BHD', symbol: 'BD', name: 'BHD - Bahraini Dinar (BD)' },
    { code: 'OMR', symbol: 'RO', name: 'OMR - Omani Rial (RO)' },
    { code: 'MYR', symbol: 'RM', name: 'MYR - Malaysian Ringgit (RM)' },
    { code: 'THB', symbol: '฿', name: 'THB - Thai Baht (฿)' },
    { code: 'IDR', symbol: 'Rp', name: 'IDR - Indonesian Rupiah (Rp)' },
    { code: 'PHP', symbol: '₱', name: 'PHP - Philippine Peso (₱)' },
    { code: 'VND', symbol: '₫', name: 'VND - Vietnamese Dong (₫)' },
    { code: 'TRY', symbol: '₺', name: 'TRY - Turkish Lira (₺)' },
    { code: 'RUB', symbol: '₽', name: 'RUB - Russian Ruble (₽)' },
    { code: 'PLN', symbol: 'zł', name: 'PLN - Polish Zloty (zł)' },
    { code: 'CZK', symbol: 'Kč', name: 'CZK - Czech Koruna (Kč)' },
    { code: 'HUF', symbol: 'Ft', name: 'HUF - Hungarian Forint (Ft)' },
    { code: 'ILS', symbol: '₪', name: 'ILS - Israeli Shekel (₪)' },
    { code: 'EGP', symbol: 'E£', name: 'EGP - Egyptian Pound (E£)' },
    { code: 'NGN', symbol: '₦', name: 'NGN - Nigerian Naira (₦)' },
    { code: 'KES', symbol: 'KSh', name: 'KES - Kenyan Shilling (KSh)' },
    { code: 'CLP', symbol: 'CLP$', name: 'CLP - Chilean Peso (CLP$)' },
    { code: 'COP', symbol: 'COL$', name: 'COP - Colombian Peso (COL$)' },
    { code: 'PEN', symbol: 'S/', name: 'PEN - Peruvian Sol (S/)' },
    { code: 'ARS', symbol: 'AR$', name: 'ARS - Argentine Peso (AR$)' }
];

const CompanyManagement = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        currency: 'USD',
        maxUsers: 20,
        status: 'Active'
    });

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole !== 'superadmin') {
            navigate('/admin-dashboard', { replace: true });
            return;
        }
        fetchCompanies();
    }, [navigate]);

    const fetchCompanies = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${serverUrl}/api/companies`);
            if (res.ok) {
                const data = await res.json();
                setCompanies(data);
            }
        } catch (err) {
            console.log('Error fetching companies from server, using local fallback');
            // Fallback seed companies
            setCompanies([
                {
                    _id: 'comp_1',
                    name: 'Federal Steel Industries',
                    code: 'FSI-001',
                    contactEmail: 'contact@federalsteel.com',
                    contactPhone: '+1 800 555 0192',
                    address: '100 Industrial Parkway, PA',
                    currency: 'USD',
                    maxUsers: 20,
                    status: 'Active',
                    createdAt: new Date().toISOString()
                },
                {
                    _id: 'comp_2',
                    name: 'Apex Alloys Manufacturing',
                    code: 'AAM-002',
                    contactEmail: 'admin@apexalloys.com',
                    contactPhone: '+1 800 555 0843',
                    address: '450 Smelter Ave, OH',
                    currency: 'USD',
                    maxUsers: 20,
                    status: 'Active',
                    createdAt: new Date().toISOString()
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (company = null, readOnly = false) => {
        setError('');
        setIsReadOnly(readOnly);
        if (company) {
            setEditingCompany(company);
            setFormData({
                name: company.name || '',
                code: company.code || '',
                contactEmail: company.contactEmail || '',
                contactPhone: company.contactPhone || '',
                address: company.address || '',
                currency: company.currency || 'USD',
                maxUsers: company.maxUsers !== undefined ? company.maxUsers : 20,
                status: company.status || 'Active'
            });
        } else {
            setEditingCompany(null);
            setFormData({
                name: '',
                code: '',
                contactEmail: '',
                contactPhone: '',
                address: '',
                currency: 'USD',
                maxUsers: 20,
                status: 'Active'
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCompany(null);
        setIsReadOnly(false);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim() || !formData.code.trim()) {
            setError('Company Name and Code are required.');
            return;
        }

        const payload = {
            name: formData.name.trim(),
            code: formData.code.trim().toUpperCase(),
            contactEmail: formData.contactEmail.trim(),
            contactPhone: formData.contactPhone.trim(),
            address: formData.address.trim(),
            currency: formData.currency || 'USD',
            maxUsers: Number(formData.maxUsers) || 20,
            status: formData.status
        };

        try {
            const url = editingCompany
                ? `${serverUrl}/api/companies/${editingCompany._id}`
                : `${serverUrl}/api/companies`;
            const method = editingCompany ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errData = await res.json();
                setError(errData.message || 'Failed to save company profile.');
                return;
            }

            const savedData = await res.json();
            if (editingCompany) {
                setEditingCompany(savedData);
                setCompanies(prev => prev.map(c => c._id === savedData._id ? savedData : c));
                setIsReadOnly(true);
            } else {
                fetchCompanies();
                handleCloseModal();
            }
        } catch (err) {
            // Local state fallback update
            if (editingCompany) {
                const updatedObj = { ...editingCompany, ...payload };
                setEditingCompany(updatedObj);
                setCompanies(prev => prev.map(c => c._id === editingCompany._id ? updatedObj : c));
                setIsReadOnly(true);
            } else {
                setCompanies(prev => [{ _id: `comp_${Date.now()}`, ...payload, createdAt: new Date().toISOString() }, ...prev]);
                handleCloseModal();
            }
        }
    };

    const handleDeleteCompany = async (companyId) => {
        if (!window.confirm('Are you sure you want to delete this company profile?')) return;

        try {
            await fetch(`${serverUrl}/api/companies/${companyId}`, { method: 'DELETE' });
            setCompanies(prev => prev.filter(c => c._id !== companyId));
        } catch (err) {
            setCompanies(prev => prev.filter(c => c._id !== companyId));
        }
    };

    const filteredCompanies = companies.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.currency && c.currency.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.contactEmail && c.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="superadmin-page">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <button onClick={() => navigate('/superadmin-dashboard')} className="btn-secondary back-btn">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div>
                        <h2 className="text-gradient" style={{ margin: 0, fontSize: '24px' }}>Company Setup</h2>
                    </div>
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleOpenModal(null, false)}
                    className="btn-primary"
                >
                    <Plus size={18} /> Add New Company
                </motion.button>
            </div>

            {/* Search & Action Bar */}
            <div className="search-filter-bar">
                <div className="search-input-wrapper">
                    <Search className="search-icon-inside" size={18} />
                    <input
                        type="text"
                        className="input-field"
                        style={{ paddingLeft: '42px' }}
                        placeholder="Search company name, code, currency, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Companies Table Panel */}
            <div className="table-panel glass-panel">
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th>Company Name</th>
                            <th>Code</th>
                            <th>Currency</th>
                            <th>User Limit</th>
                            <th>Contact Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCompanies.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                                    No company records found. Click "Add New Company" to set up an organization.
                                </td>
                            </tr>
                        ) : (
                            filteredCompanies.map(c => (
                                <tr key={c._id} className="clickable-row" onClick={() => handleOpenModal(c, true)}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                                            <Building2 size={18} color="var(--primary-color)" />
                                            <span>{c.name}</span>
                                            {c.address && (
                                                <span 
                                                    className="address-icon-badge" 
                                                    title={`${c.address}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <MapPin size={13} />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="company-code-badge">{c.code}</span>
                                    </td>
                                    <td>
                                        <span className="company-code-badge" style={{ background: 'rgba(14, 165, 233, 0.1)', color: 'var(--primary-color)', borderColor: 'rgba(14, 165, 233, 0.25)' }}>
                                            {c.currency || 'USD'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="company-code-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--secondary-color)', borderColor: 'rgba(99, 102, 241, 0.25)' }}>
                                            {c.maxUsers !== undefined ? c.maxUsers : 20} Users
                                        </span>
                                    </td>
                                    <td>{c.contactEmail || '—'}</td>
                                    <td>{c.contactPhone || '—'}</td>
                                    <td>
                                        <span className={`status-tag ${c.status?.toLowerCase()}`}>
                                            {c.status || 'Active'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/superadmin/admins?companyId=${c._id}`);
                                                }} 
                                                className="icon-btn" 
                                                title="View Admin Users"
                                            >
                                                <Users size={16} />
                                            </button>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/superadmin/subscriptions/company/${c._id}`);
                                                }} 
                                                className="icon-btn" 
                                                title="Subscription Details"
                                            >
                                                <CreditCard size={16} />
                                            </button>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenModal(c, false);
                                                }} 
                                                className="icon-btn" 
                                                title="Edit Company"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteCompany(c._id);
                                                }} 
                                                className="icon-btn delete" 
                                                title="Delete Company"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Read-Only Details / Edit Company Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="modal-overlay">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="modal-card glass-panel"
                        >
                            <div className="modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <h3 className="text-gradient" style={{ margin: 0 }}>
                                        {isReadOnly ? 'Company Details' : editingCompany ? 'Edit Company Profile' : 'Register New Company'}
                                    </h3>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {isReadOnly && editingCompany && (
                                        <button
                                            type="button"
                                            onClick={() => setIsReadOnly(false)}
                                            className="btn-primary"
                                            style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <Edit2 size={14} /> Edit Details
                                        </button>
                                    )}
                                    <button onClick={handleCloseModal} className="icon-btn" title="Close">
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            {isReadOnly ? (
                                <div className="read-only-details">
                                    <div className="read-only-grid">
                                        <div className="read-only-field form-full">
                                            <span className="read-only-label">Company Name</span>
                                            <div className="read-only-value-large">
                                                <Building2 size={20} color="var(--primary-color)" />
                                                <span>{formData.name}</span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Company Code</span>
                                            <div className="read-only-value">
                                                <span className="company-code-badge">{formData.code}</span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Operating Currency</span>
                                            <div className="read-only-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Globe size={16} color="var(--primary-color)" />
                                                <span className="company-code-badge" style={{ background: 'rgba(14, 165, 233, 0.1)', color: 'var(--primary-color)', borderColor: 'rgba(14, 165, 233, 0.25)' }}>
                                                    {formData.currency || 'USD'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Max Users Limit</span>
                                            <div className="read-only-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Users size={16} color="var(--secondary-color)" />
                                                <span className="company-code-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--secondary-color)', borderColor: 'rgba(99, 102, 241, 0.25)' }}>
                                                    {formData.maxUsers} Users Max
                                                </span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Status</span>
                                            <div className="read-only-value">
                                                <span className={`status-tag ${formData.status?.toLowerCase()}`}>
                                                    {formData.status}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Contact Email</span>
                                            <div className="read-only-value">{formData.contactEmail || '—'}</div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Contact Phone</span>
                                            <div className="read-only-value">{formData.contactPhone || '—'}</div>
                                        </div>

                                        <div className="read-only-field form-full">
                                            <span className="read-only-label">Address</span>
                                            <div className="read-only-value" style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                {formData.address && <MapPin size={16} color="var(--text-secondary)" style={{ flexShrink: 0, marginTop: '2px' }} />}
                                                <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{formData.address || '—'}</span>
                                            </div>
                                        </div>

                                        {editingCompany?.createdAt && (
                                            <div className="read-only-field form-full">
                                                <span className="read-only-label">Registered Date</span>
                                                <div className="read-only-value" style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                                                    {new Date(editingCompany.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--panel-border)' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleCloseModal();
                                                    navigate(`/superadmin/admins?companyId=${editingCompany._id}`);
                                                }}
                                                className="btn-secondary"
                                                style={{ padding: '6px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <Users size={14} /> View Admins
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleCloseModal();
                                                    navigate(`/superadmin/subscriptions/company/${editingCompany._id}`);
                                                }}
                                                className="btn-secondary"
                                                style={{ padding: '6px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                            >
                                                <CreditCard size={14} /> Subscriptions
                                            </button>
                                        </div>
                                        <button type="button" onClick={handleCloseModal} className="btn-secondary">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="form-grid">
                                    <div className="form-full">
                                        <label className="field-label">Company Name *</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="e.g. Federal Steel Corp"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="field-label">Company Code *</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="e.g. FSC-01"
                                            value={formData.code}
                                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="field-label">Operating Currency *</label>
                                        <select
                                            className="input-field"
                                            value={formData.currency}
                                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                            required
                                        >
                                            {CURRENCY_OPTIONS.map(c => (
                                                <option key={c.code} value={c.code}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="field-label">Max Users Limit *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className="input-field"
                                            placeholder="Default: 20"
                                            value={formData.maxUsers}
                                            onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="field-label">Status</label>
                                        <select
                                            className="input-field"
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="field-label">Contact Email</label>
                                        <input
                                            type="email"
                                            className="input-field"
                                            placeholder="e.g. contact@company.com"
                                            value={formData.contactEmail}
                                            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="field-label">Contact Phone</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="e.g. +1 800 555 0192"
                                            value={formData.contactPhone}
                                            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                        />
                                    </div>

                                    <div className="form-full">
                                        <label className="field-label">Address</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="e.g. 100 Industrial Parkway, PA"
                                            value={formData.address}
                                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        />
                                    </div>

                                    {error && (
                                        <div className="form-full" style={{ color: 'var(--error-color)', fontSize: '13px', background: '#fee2e2', padding: '8px 12px', borderRadius: '6px' }}>
                                            {error}
                                        </div>
                                    )}

                                    <div className="form-full" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (editingCompany) {
                                                    setIsReadOnly(true);
                                                } else {
                                                    handleCloseModal();
                                                }
                                            }}
                                            className="btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn-primary">
                                            {editingCompany ? 'Save Changes' : 'Create Company'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CompanyManagement;

